"use client";

import { ArrowLeft, } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import useApi from "@src/api_service/useApi";
import {  Form, } from "antd";
import LoadingStateComp from "@src/components/LoadingComponent/LoadingComponent";
import { ThirdPartyRequestData } from "../interface";
import { getDateFullFormat } from "@src/utils/app_functions";

interface ThirdPartyRequestDetailViewProps {
  resource: ThirdPartyRequestData;
  onGoBack?: () => void;
  onChangeStatus?: () => void;
}

export const ThirdPartyRequestDetailView: React.FC<ThirdPartyRequestDetailViewProps> = ({
  resource,
  onGoBack,
  onChangeStatus,
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { loading: changeStatusLoading, post: changeStatusPost } = useApi();

  // close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleStatusChange = (statusValue: "approved" | "rejected") => {
    // Get authentication token from localStorage
    const token = localStorage.getItem("token");

    const formData = new FormData();
    formData.append("requestId", resource?._id);
    formData.append("status", statusValue);

    changeStatusPost(
      "/update_publish_request",
      formData,
      {
        Authorization: `Bearer ${token}`,
      },
      {
        method: "PATCH",
        onSuccess: (data) => {
          if (onChangeStatus) onChangeStatus();
        },
        onError: (error: any) => {
          console.log(error);
        },
      }
    );
  };

  const handleSelect = (option: "approved" | "rejected") => {
    setOpen(false);
    handleStatusChange(option);
  };


  if (changeStatusLoading) {
    return <LoadingStateComp Message="" />;
    }

  return (
    <div className="h-auto py-6 ">
      <div className="max-w-4xl pt-6 mx-auto">
        <div className="flex justify-between mb-[5px]">
          {/* Go Back Button */}
          <button
            onClick={onGoBack}
            className="flex items-center gap-2 mb-6 text-gray-900 transition-colors hover:text-gray-700"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Go Back</span>
          </button>

          <div className="relative inline-block" ref={dropdownRef}>
            <button
              onClick={() => {
                setOpen(true);
              }}
              className="bg-[#EC5306] hover:bg-orange-700 text-white px-8 py-2 rounded-[8px] text-base font-medium transition-colors"
            >
              {"Change Status"}
            </button>
            {open && (
              <div className="absolute z-10 mt-2 w-48 px-2 bg-white rounded-[8px] shadow-lg">
                <ul className="py-1">
                  <li
                    onClick={() => handleSelect("approved")}
                    className="block rounded-lg p-2 text-[#686677] text-xs hover:text-white hover:bg-[#1E2A58]"
                  >
                    Approve
                  </li>
                  <li
                    onClick={() => handleSelect("rejected")}
                    className="block rounded-lg p-2 text-[#686677] text-xs hover:text-white hover:bg-[#1E2A58]"
                  >
                    Reject
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Resource Detail Card */}
        <div className="bg-white rounded-[12px] border border-[#EFEEEEBF] bg-[#A0AEC003] shadow-sm p-8 my-10">
          {/* Type of Resource */}
          <div className="mb-6">
            <h3 className="mb-1 text-sm font-semibold text-gray-900">
              App Name
            </h3>
            <p className="text-sm text-[#A0AEC0] font-inter font-normal leading-[18px] tracking-[-0.02em]">
              {resource.systemApp?.appname ?? "N/A"}
            </p>
          </div>

          {/* Name of Resource */}
          <div className="mb-6">
            <h3 className="mb-1 text-sm font-semibold text-gray-900">
              Organization Name
            </h3>
            <p className="text-sm text-[#A0AEC0] font-inter font-normal leading-[18px] tracking-[-0.02em]">
              {resource.thirdPartyApp?.organizationName ?? "N/A"}
            </p>
          </div>

          {/* Class */}
          <div className="mb-6">
            <h3 className="mb-1 text-sm font-semibold text-gray-900">
              Email
            </h3>
            <p className="text-sm text-[#A0AEC0] font-inter font-normal leading-[18px] tracking-[-0.02em]">
              {resource.thirdPartyApp?.email ?? "N/A"}
            </p>
          </div>

          {/* Description */}
          <div className="mb-6">
            <h3 className="mb-2 text-sm font-semibold text-gray-900">
              Domain
            </h3>
            {resource.systemApp?.domain ? (
              <a 
                href={`https://${resource.systemApp.domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#A0AEC0] font-inter font-normal leading-[18px] tracking-[-0.02em] underline cursor-pointer hover:text-blue-600 transition-colors"
              >
                {resource.systemApp.domain}
              </a>
            ) : (
              <p className="text-sm text-[#A0AEC0] font-inter font-normal leading-[18px] tracking-[-0.02em]">
                N/A
              </p>
            )}
          </div>

          <div className="mb-6">
            <h3 className="mb-2 text-sm font-semibold text-gray-900">
              Billing Type
            </h3>
            <p className="text-sm uppercase text-[#A0AEC0] font-inter font-normal leading-[18px] tracking-[-0.02em]">
              { resource.systemApp?.biilingType ?? "" }
            </p>
          </div>

          { resource.systemApp?.biilingType == "postpaid" && <div className="mb-6">
            <h3 className="mb-2 text-sm font-semibold text-gray-900">
              Plan name
            </h3>
            <p className="text-sm uppercase text-[#A0AEC0] font-inter font-normal leading-[18px] tracking-[-0.02em]">
              { resource.systemApp?.postpaidPlanId?.planName ?? "" }
            </p>
          </div>}

          { resource.systemApp?.biilingType == 'prepaid' && <div className="mb-6">
            <h3 className="mb-2 text-sm font-semibold text-gray-900">
              Wallet Balance
            </h3>
            <p className="text-sm uppercase text-[#A0AEC0] font-inter font-normal leading-[18px] tracking-[-0.02em]">
              {resource.systemApp?.walletCurrency ?? ''} { resource.systemApp?.walletBalance ?? "" }
            </p>
          </div>}

          

          <div className="mb-6">
            <h3 className="mb-2 text-sm font-semibold text-gray-900">
              Requested Date
            </h3>
            <p className="text-sm text-[#A0AEC0] font-inter font-normal leading-[18px] tracking-[-0.02em]">
              { resource.createdAt ? getDateFullFormat(resource.createdAt) : "N/A"}
            </p>
          </div>

          { resource.systemApp?.publishedStatus == "published" && (
            <div className="mb-6">
              <h3 className="mb-2 text-sm font-semibold text-gray-900">
                Published Date
              </h3>
              <p className="text-sm text-[#A0AEC0] font-inter font-normal leading-[18px] tracking-[-0.02em]">
                { resource.updatedAt ? getDateFullFormat(resource.updatedAt) : "N/A"}
              </p>
            </div>
          )}

          <div className="flex justify-center mb-6 rounded-[10px]">
            { (resource.systemApp?.icon || resource.systemApp?.appLogo) && (
              <img
                src={resource.systemApp?.icon ?? resource.systemApp?.appLogo}
                alt={resource.systemApp?.appname}
                className="max-w-full max-h-[60vh] object-contain"
              />
            )}
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-4 mb-8  border border-[#E9EAEC] bg-[#FFFFFF] rounded-[10px] py-4 px-5 ">
            <div className="text-center">
              <p className="mb-1 text-xs text-gray-500">Contact Number</p>
              <p className="text-sm font-semibold text-gray-900">
                {resource.thirdPartyApp?.contactNumber ?? "N/A"}
              </p>
            </div>
            <div className="text-center">
              <p className="mb-1 text-xs text-gray-500">App Model Name</p>
              <p className="text-sm font-semibold text-gray-900">
                {resource.systemApp?.appModelName ?? "N/A"}
              </p>
            </div>
          </div>
        </div>

        
      </div>
    </div>
  );
};
